import type { CostEstimateData } from "./types";

/**
 * 이미지(공사원가계산서)에서 읽어낸 샘플 데이터.
 * 추후 엑셀 업로드 / DB 연동 시 이 객체만 실제 데이터로 교체하면 됩니다.
 */
export const SAMPLE_COST_ESTIMATE: CostEstimateData = {
  directMaterial: [
    {
      code: "01",
      name: "환경개선공사",
      midWorks: [
        { code: "0101", name: "철거공사", items: [] },
        {
          code: "0102",
          name: "창호공사",
          items: [
            {
              id: "m-0102-1",
              name: "알루미늄창호 (재료)",
              unit: "개",
              quantity: 10,
              unitPrice: 120000,
            },
            {
              id: "m-0102-2",
              name: "방충망 (재료)",
              unit: "개",
              quantity: 10,
              unitPrice: 15000,
            },
          ],
        },
        {
          code: "0103",
          name: "기타공사",
          items: [
            {
              id: "m-0103-1",
              name: "도배지 (재료)",
              unit: "m²",
              quantity: 50,
              unitPrice: 5000,
            },
            {
              id: "m-0103-2",
              name: "바닥재 (재료)",
              unit: "m²",
              quantity: 20,
              unitPrice: 18000,
            },
          ],
        },
        { code: "0104", name: "폐기물처리비", items: [] },
      ],
    },
    {
      code: "02",
      name: "안전시설공사",
      midWorks: [
        {
          code: "0201",
          name: "소화설비공사",
          items: [
            {
              id: "m-0201-1",
              name: "스프링클러 헤드 (재료)",
              unit: "개",
              quantity: 10,
              unitPrice: 50000,
            },
            {
              id: "m-0201-2",
              name: "배관 자재 (재료)",
              unit: "m",
              quantity: 20,
              unitPrice: 12000,
            },
          ],
        },
        {
          code: "0202",
          name: "비상조명공사",
          items: [
            {
              id: "m-0202-1",
              name: "비상구유도등 (재료)",
              unit: "개",
              quantity: 5,
              unitPrice: 60000,
            },
            {
              id: "m-0202-2",
              name: "비상조명등 (재료)",
              unit: "개",
              quantity: 5,
              unitPrice: 40000,
            },
          ],
        },
      ],
    },
  ],

  // 직접노무비: 인건 품목 (합계 2,955,000)
  directLabor: [
    {
      code: "01",
      name: "환경개선공사",
      midWorks: [
        {
          code: "0101",
          name: "철거공사",
          items: [
            {
              id: "l-0101-1",
              name: "콘크리트벽체철거 (인건)",
              unit: "m²",
              quantity: 50,
              unitPrice: 15000,
            },
            {
              id: "l-0101-2",
              name: "바닥철거 (인건)",
              unit: "m²",
              quantity: 30,
              unitPrice: 12000,
            },
            {
              id: "l-0101-3",
              name: "천장철거 (인건)",
              unit: "m²",
              quantity: 25,
              unitPrice: 9000,
            },
            {
              id: "l-0101-4",
              name: "창호철거 (인건)",
              unit: "식",
              quantity: 5,
              unitPrice: 60000,
            },
          ],
        },
        {
          code: "0102",
          name: "창호공사",
          items: [
            {
              id: "l-0102-1",
              name: "창호 설치 (인건)",
              unit: "개",
              quantity: 10,
              unitPrice: 45000,
            },
            {
              id: "l-0102-2",
              name: "방충망 설치 (인건)",
              unit: "개",
              quantity: 10,
              unitPrice: 10000,
            },
          ],
        },
        {
          code: "0103",
          name: "기타공사",
          items: [
            {
              id: "l-0103-1",
              name: "도배 시공 (인건)",
              unit: "m²",
              quantity: 50,
              unitPrice: 4000,
            },
            {
              id: "l-0103-2",
              name: "바닥 시공 (인건)",
              unit: "m²",
              quantity: 20,
              unitPrice: 6000,
            },
          ],
        },
        { code: "0104", name: "폐기물처리비", items: [] },
      ],
    },
    {
      code: "02",
      name: "안전시설공사",
      midWorks: [
        {
          code: "0201",
          name: "소화설비공사",
          items: [
            {
              id: "l-0201-1",
              name: "스프링클러 설치 (인건)",
              unit: "개",
              quantity: 10,
              unitPrice: 30000,
            },
          ],
        },
        {
          code: "0202",
          name: "비상조명공사",
          items: [
            {
              id: "l-0202-1",
              name: "비상조명 설치 (인건)",
              unit: "개",
              quantity: 10,
              unitPrice: 15000,
            },
          ],
        },
      ],
    },
  ],

  // 간접비 요율
  indirectMaterialRate: 0.0282, // 직접재료비 × 2.82%
  indirectLaborRate: 0.175, // 직접노무비 × 17.5%

  // 경비 (계산 순서대로 — 환경보전비는 앞선 경비 합을 사용하므로 맨 뒤)
  expenses: [
    {
      id: "exp-sanjae",
      name: "산재보험료",
      baseLabel: "노무비",
      base: "labor",
      rate: 0.0356,
    },
    {
      id: "exp-goyong",
      name: "고용보험료",
      baseLabel: "노무비",
      base: "labor",
      rate: 0.0101,
    },
    {
      id: "exp-seokmyeon",
      name: "석면분담금",
      baseLabel: "노무비",
      base: "labor",
      rate: 0.00006,
    },
    {
      id: "exp-imgeum",
      name: "임금채권부담금",
      baseLabel: "노무비",
      base: "labor",
      rate: 0.0009,
    },
    {
      id: "exp-gita",
      name: "기타경비",
      baseLabel: "재료비+노무비",
      base: "materialLabor",
      rate: 0.05,
    },
    {
      id: "exp-hwangyeong",
      name: "환경보전비",
      baseLabel: "재료비+노무비+경비",
      base: "materialLaborExpensePrior",
      rate: 0.003,
    },
  ],

  // 순공사원가(계) 이후
  generalAdminRate: 0.08, // 일반관리비: 계 × 8%
  profitRate: 0.15, // 이윤: 노무+경비+일반관리비 × 15%
  wasteDisposalAmount: 50000, // 폐기물처리비 (합계제외)
  vatRate: 0.1, // 부가가치세: 공급가액 × 10%
};
